/**
 * api.ts - Typed API client for the backend.
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: `HTTP ${res.status}` }));
    throw new Error((err as { message?: string }).message ?? `Request failed: ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ---- Contact ----

export interface ContactPayload {
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
}

export interface ContactResponse {
  success: boolean;
  message: string;
}

export async function submitContact(payload: ContactPayload): Promise<ContactResponse> {
  return apiFetch<ContactResponse>('/api/contact', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ---- Lead ----

export interface LeadPayload {
  name?: string;
  email: string;
  phone?: string;
  source?: string;
  tags?: string[];
}

export interface LeadResponse {
  success: boolean;
  message: string;
}

export async function submitLead(payload: LeadPayload): Promise<LeadResponse> {
  return apiFetch<LeadResponse>('/api/lead', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ---- Booking ----

export interface BookingSlot {
  datetime: string;
  available: boolean;
  duration_minutes: number;
}

export async function getBookingSlots(): Promise<BookingSlot[]> {
  const data = await apiFetch<{ slots: BookingSlot[] }>('/api/booking');
  return data.slots;
}

export interface BookingPayload {
  name: string;
  email: string;
  datetime: string;
  notes?: string;
}

export interface BookingResponse {
  success: boolean;
  message: string;
  bookingId: string;
}

export async function createBooking(payload: BookingPayload): Promise<BookingResponse> {
  return apiFetch<BookingResponse>('/api/booking', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ---- Health ----

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
}

export async function checkHealth(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>('/api/health');
}
